import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  type SubmitGiftCardOrderUsingPaymentMethodMutationVariables,
  useSubmitGiftCardOrderUsingPaymentMethodMutation,
} from '../../../graphql/GiftCardCheckout.generated';
import { giftCardCheckoutMessages as messages } from './useGiftCardCheckout.messages';

// ────────────────────────────────────────────────────────────────────────────────

export const useGiftCardCheckout = () => {
  const [, submitGiftCard] = useSubmitGiftCardOrderUsingPaymentMethodMutation();

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner, remove: removeNoticeBanner } = useNoticeBannersStackActions();

  const router = useRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Displays a custom error notice banner with predefined options.
   */
  const onSubmissionError = useCallback(
    (payload: OnSubmissionErrorPayload) => {
      const { userError, systemError } = payload;

      track('gift.purchase_failure', { userError, systemError });

      const bannerText =
        userError === 'SubmitGiftCardOrderPaymentError'
          ? messages.paymentErrorBannerText
          : messages.errorBannerText;

      addNoticeBanner(
        {
          id: SUBMISSION_ERROR_NOTICE_BANNER_ID,
          text: formatMessage(bannerText),
          palette: 'caution',
        },
        true,
      );
    },
    [addNoticeBanner, formatMessage, track],
  );

  /**
   * Removes a pending error notice banner.
   */
  const removeErrorNoticeBanner = useCallback(() => {
    removeNoticeBanner(SUBMISSION_ERROR_NOTICE_BANNER_ID);
  }, [removeNoticeBanner]);

  const onSubmissionSuccess = useCallback(
    (orderId: string) => {
      track('gift.purchase_success');

      router.navigate(`/account/gift/confirmation/${orderId}`);
    },
    [router, track],
  );

  const checkoutGiftCard = useCallback(
    async (
      variables: SubmitGiftCardOrderUsingPaymentMethodMutationVariables,
      onError?: () => void,
    ) => {
      removeErrorNoticeBanner();

      const { data, error } = await submitGiftCard(variables);

      const response = data?.submitGiftCardOrderUsingPaymentMethod;
      const responseTypename = response?.__typename;

      // ─────────────────────────────────────────────────────────────────────

      const hasNetworkError = error !== undefined;
      const hasSubmittedSuccessfully =
        responseTypename === 'GiftCardOrderDetail' && response?.id !== undefined;
      const hasFailedToSubmit = hasNetworkError || !hasSubmittedSuccessfully;

      // ─────────────────────────────────────────────────────────────────────

      if (hasFailedToSubmit) {
        onError?.();
        onSubmissionError({ userError: responseTypename, systemError: error });

        return;
      }

      if (hasSubmittedSuccessfully) {
        onSubmissionSuccess(response.id);
      }

      return data;
    },
    [removeErrorNoticeBanner, submitGiftCard, onSubmissionError, onSubmissionSuccess],
  );

  return { checkoutGiftCard };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SUBMISSION_ERROR_NOTICE_BANNER_ID = 'gift-card-checkout-submit-error';

// ─── Types ───────────────────────────────────────────────────────────────────

type OnSubmissionErrorPayload = {
  systemError: unknown | undefined;
  userError: string | undefined;
};
